import { ALPHALEND_WS_URL } from '../utils/config';
class AlphalendWebSocketService {
    constructor() {
        this.ws = null;
        this.roomCallbacks = new Map();
        this.roomMessageCallbacks = new Map();
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.isUserActive = true;
        this.isTabVisible = true;
        this.activityTimer = null;
        this.connectionStatusCallbacks = new Set();
        this.connectionState = 'disconnected';
        this.connectionTimeout = null;
        this.heartbeatInterval = null;
        this.subscriptionQueue = new Set();
        this.activeSubscriptions = new Map();
        this.messageQueue = [];
        this.reconnectDelay = 1000; // Base delay for exponential backoff
        this.maxReconnectDelay = 30000; // Max 30 seconds
        this.lastHeartbeat = 0;
        this.isDestroyed = false;
        this.messageDeduplication = new Map();
        this.debounceTimers = new Map();
        this.debounceDelay = 100; // 100ms debounce for rapid updates
        this.deduplicationWindow = 5000; // 5 seconds for message deduplication
        this.isOnline = navigator.onLine;
        this.offlineQueue = [];
        this.maxOfflineQueueSize = 100;
        this.messageListeners = new Map();
        this.setupActivityDetection();
        this.setupVisibilityDetection();
        this.setupBeforeUnload();
        this.setupOfflineDetection();
    }
    setupBeforeUnload() {
        // Clean up on page unload
        window.addEventListener('beforeunload', () => {
            this.destroy();
        });
    }
    setupOfflineDetection() {
        window.addEventListener('online', () => {
            this.isOnline = true;
            this.processOfflineQueue();
            this.checkConnectionState();
        });
        window.addEventListener('offline', () => {
            this.isOnline = false;
            // Don't disconnect immediately - let the connection timeout handle it
        });
    }
    destroy() {
        this.isDestroyed = true;
        this.clearAllTimers();
        this.clearDebounceTimers();
        this.disconnect();
        this.connectionStatusCallbacks.clear();
        this.roomCallbacks.clear();
        this.roomMessageCallbacks.clear();
        this.activeSubscriptions.clear();
        this.subscriptionQueue.clear();
        this.messageQueue = [];
        this.messageDeduplication.clear();
        this.offlineQueue = [];
    }
    clearAllTimers() {
        if (this.activityTimer) {
            clearTimeout(this.activityTimer);
            this.activityTimer = null;
        }
        if (this.connectionTimeout) {
            clearTimeout(this.connectionTimeout);
            this.connectionTimeout = null;
        }
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }
    clearDebounceTimers() {
        this.debounceTimers.forEach((timer) => {
            clearTimeout(timer);
        });
        this.debounceTimers.clear();
    }
    generateMessageHash(message) {
        // Create a hash based on room, event, and data structure (not full data)
        const hashData = {
            room: message.room,
            event: message.event,
            timestamp: message.timestamp,
            dataKeys: message.data && typeof message.data === 'object' ? Object.keys(message.data).sort() : null,
        };
        return JSON.stringify(hashData);
    }
    isDuplicateMessage(roomName, message) {
        const now = Date.now();
        const messageHash = this.generateMessageHash(message);
        const key = `${roomName}:${message.event}`;
        // Clean up old deduplication entries
        this.messageDeduplication.forEach((value, mapKey) => {
            if (now - value.timestamp > this.deduplicationWindow) {
                this.messageDeduplication.delete(mapKey);
            }
        });
        const existing = this.messageDeduplication.get(key);
        if (existing && existing.hash === messageHash) {
            return true; // Duplicate message
        }
        // Store this message for future deduplication
        this.messageDeduplication.set(key, { hash: messageHash, timestamp: now });
        return false;
    }
    debounceCallback(roomName, message, callbacks, isMessageCallback = false) {
        const debounceKey = `${roomName}:${message.event}`;
        // Clear existing timer for this room/event combination
        const existingTimer = this.debounceTimers.get(debounceKey);
        if (existingTimer) {
            clearTimeout(existingTimer);
        }
        // Set new debounced timer
        const timer = setTimeout(() => {
            callbacks.forEach((callback) => {
                try {
                    if (isMessageCallback) {
                        callback(message);
                    }
                    else {
                        callback(message.data);
                    }
                }
                catch (error) {
                    console.error(`Error in debounced alphalend callback for ${roomName}:`, error);
                }
            });
            this.debounceTimers.delete(debounceKey);
        }, this.debounceDelay);
        this.debounceTimers.set(debounceKey, timer);
    }
    addToOfflineQueue(action, data) {
        if (this.offlineQueue.length >= this.maxOfflineQueueSize) {
            // Remove oldest item to make space
            this.offlineQueue.shift();
        }
        this.offlineQueue.push({
            action,
            data,
            timestamp: Date.now(),
        });
        // Added to offline queue
    }
    processOfflineQueue() {
        if (this.offlineQueue.length === 0)
            return;
        const now = Date.now();
        const maxAge = 5 * 60 * 1000; // 5 minutes
        // Filter out stale items and process valid ones
        const validItems = this.offlineQueue.filter((item) => now - item.timestamp < maxAge);
        validItems.forEach((item) => {
            var _a;
            if (((_a = this.ws) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN) {
                try {
                    this.ws.send(JSON.stringify(item.data));
                }
                catch (error) {
                    console.error(`Failed to process offline queue item: ${item.action}`, error);
                }
            }
        });
        this.offlineQueue = [];
    }
    // Public method to check if service is online
    isNetworkOnline() {
        return this.isOnline;
    }
    // Public method to get offline queue status
    getOfflineQueueStatus() {
        return {
            size: this.offlineQueue.length,
            maxSize: this.maxOfflineQueueSize,
            isOnline: this.isOnline,
        };
    }
    resetActivityTimer() {
        this.isUserActive = true;
        this.checkConnectionState();
        if (this.activityTimer)
            clearTimeout(this.activityTimer);
        this.activityTimer = setTimeout(() => {
            this.isUserActive = false;
            this.checkConnectionState();
        }, 5 * 60 * 1000); // 5 minutes
    }
    setupActivityDetection() {
        ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'].forEach((event) => {
            document.addEventListener(event, this.resetActivityTimer.bind(this), true);
        });
        this.resetActivityTimer(); // Start timer
    }
    setupVisibilityDetection() {
        document.addEventListener('visibilitychange', () => {
            this.isTabVisible = !document.hidden;
            // Treat returning to tab as user activity so we reconnect and re-fetch immediately
            if (this.isTabVisible) {
                this.resetActivityTimer();
            }
            this.checkConnectionState();
        });
    }
    checkConnectionState() {
        if (this.isDestroyed)
            return;
        const shouldBeConnected = this.isTabVisible && this.isUserActive && this.isOnline;
        if (shouldBeConnected && this.connectionState === 'disconnected') {
            this.connect();
        }
        else if (!shouldBeConnected && this.connectionState === 'connected') {
            this.disconnect();
        }
    }
    notifyConnectionStatusChange() {
        if (this.isDestroyed)
            return;
        this.connectionStatusCallbacks.forEach((callback) => {
            try {
                callback();
            }
            catch (error) {
                console.error('Error in alphalend connection status callback:', error);
            }
        });
    }
    setConnectionState(state) {
        if (this.connectionState !== state) {
            // Connection state changed
            this.connectionState = state;
            this.notifyConnectionStatusChange();
        }
    }
    saveToCache(room, message) {
        try {
            const serverTimestamp = message.timestamp * 1000;
            const now = Date.now();
            // Get existing cache to preserve event data
            const existingCache = this.getCachedData(room);
            const eventData = (existingCache === null || existingCache === void 0 ? void 0 : existingCache.eventData) || new Map();
            // Update event-specific cache if this message has an event
            if (message.event) {
                eventData.set(message.event, message.data);
            }
            const cacheData = {
                message: message,
                serverTimestamp: serverTimestamp,
                cachedAt: now,
                lastUpdated: now,
                eventData: eventData,
            };
            // Implement cache size limit (keep only last 50 entries per room)
            const cacheKey = `alphalend-ws-room-${room}`;
            localStorage.setItem(cacheKey, JSON.stringify(cacheData, this.mapReplacer));
            // Clean up old cache entries periodically
            this.cleanupOldCache();
        }
        catch (error) {
            console.error('Error saving to cache:', error);
            // If localStorage is full, try to clear some space
            this.clearOldestCacheEntries();
        }
    }
    // Helper for Map serialization
    mapReplacer(_key, value) {
        if (value instanceof Map) {
            return {
                dataType: 'Map',
                value: Array.from(value.entries()),
            };
        }
        return value;
    }
    // Helper for Map deserialization
    mapReviver(_key, value) {
        if (typeof value === 'object' &&
            value !== null &&
            value.dataType === 'Map') {
            return new Map(value.value);
        }
        return value;
    }
    cleanupOldCache() {
        try {
            const now = Date.now();
            const maxAge = 24 * 60 * 60 * 1000; // 24 hours
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key === null || key === void 0 ? void 0 : key.startsWith('alphalend-ws-room-')) {
                    const cached = localStorage.getItem(key);
                    if (cached) {
                        try {
                            const data = JSON.parse(cached, this.mapReviver);
                            if (now - data.cachedAt > maxAge) {
                                localStorage.removeItem(key);
                            }
                        }
                        catch (_a) {
                            localStorage.removeItem(key);
                        }
                    }
                }
            }
        }
        catch (error) {
            console.error('Error cleaning up cache:', error);
        }
    }
    clearOldestCacheEntries() {
        try {
            const cacheEntries = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key === null || key === void 0 ? void 0 : key.startsWith('alphalend-ws-room-')) {
                    const cached = localStorage.getItem(key);
                    if (cached) {
                        try {
                            const data = JSON.parse(cached, this.mapReviver);
                            cacheEntries.push({ key, cachedAt: data.cachedAt });
                        }
                        catch (_a) {
                            localStorage.removeItem(key);
                        }
                    }
                }
            }
            // Remove oldest 25% of entries
            cacheEntries.sort((a, b) => a.cachedAt - b.cachedAt);
            const toRemove = Math.ceil(cacheEntries.length * 0.25);
            for (let i = 0; i < toRemove; i++) {
                localStorage.removeItem(cacheEntries[i].key);
            }
        }
        catch (error) {
            console.error('Error clearing oldest cache entries:', error);
        }
    }
    getCachedData(room) {
        const cached = localStorage.getItem(`alphalend-ws-room-${room}`);
        if (!cached)
            return null;
        try {
            const cacheEntry = JSON.parse(cached, this.mapReviver);
            // Ensure we have valid data and timestamp
            if (!cacheEntry.message || typeof cacheEntry.serverTimestamp !== 'number') {
                localStorage.removeItem(`alphalend-ws-room-${room}`);
                return null;
            }
            // Calculate data age based on server timestamp
            const dataAge = Date.now() - cacheEntry.serverTimestamp;
            const isStale = dataAge > 60000; // Data older than 1 minute from server
            return {
                message: cacheEntry.message,
                serverTimestamp: cacheEntry.serverTimestamp,
                isStale,
                eventData: cacheEntry.eventData,
            };
        }
        catch (error) {
            console.error('Error parsing alphalend cached data:', error);
            localStorage.removeItem(`alphalend-ws-room-${room}`);
            return null;
        }
    }
    // New method to get cached event data
    getCachedEventData(room, eventName) {
        const cached = this.getCachedData(room);
        if (!(cached === null || cached === void 0 ? void 0 : cached.eventData) || !(cached.eventData instanceof Map)) {
            return null;
        }
        return cached.eventData.get(eventName) || null;
    }
    connect() {
        if (this.isDestroyed)
            return;
        // Prevent multiple connection attempts
        if (this.connectionState === 'connecting' || this.connectionState === 'connected') {
            return;
        }
        // Clean up existing connection
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.clearAllTimers();
        this.setConnectionState('connecting');
        try {
            this.ws = new WebSocket(ALPHALEND_WS_URL);
            // Set connection timeout
            this.connectionTimeout = setTimeout(() => {
                if (this.connectionState === 'connecting') {
                    console.error('AlphaLend WebSocket connection timeout');
                    this.handleConnectionFailure();
                }
            }, 10000); // 10 second timeout
        }
        catch (error) {
            console.error('Failed to create AlphaLend WebSocket:', error);
            this.handleConnectionFailure();
            return;
        }
        this.setupWebSocketHandlers();
    }
    handleConnectionFailure() {
        this.clearAllTimers();
        this.setConnectionState('failed');
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.scheduleReconnect();
    }
    scheduleReconnect() {
        if (this.isDestroyed || this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('Max reconnection attempts reached or service destroyed');
            return;
        }
        this.reconnectAttempts++;
        this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay);
        // Scheduling reconnection
        setTimeout(() => {
            if (this.isTabVisible && this.isUserActive && !this.isDestroyed) {
                this.setConnectionState('reconnecting');
                this.connect();
            }
        }, this.reconnectDelay);
    }
    startHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
        }
        this.heartbeatInterval = setInterval(() => {
            var _a;
            if (this.connectionState === 'connected' && ((_a = this.ws) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN) {
                const now = Date.now();
                if (now - this.lastHeartbeat > 30000) {
                    // 30 seconds without message
                    console.warn('AlphaLend WebSocket heartbeat timeout, reconnecting...');
                    this.handleConnectionFailure();
                }
            }
        }, 15000); // Check every 15 seconds
    }
    processSubscriptionQueue() {
        // Re-subscribe to all active rooms
        this.roomCallbacks.forEach((callbacks, room) => {
            if (callbacks.size > 0) {
                this.subscriptionQueue.add(room);
            }
        });
        this.roomMessageCallbacks.forEach((callbacks, room) => {
            if (callbacks.size > 0) {
                this.subscriptionQueue.add(room);
            }
        });
        // Send all queued subscriptions
        this.subscriptionQueue.forEach((room) => {
            this.sendSubscription(room);
        });
        this.subscriptionQueue.clear();
    }
    processMessageQueue() {
        var _a;
        // Process any queued messages
        while (this.messageQueue.length > 0) {
            const { message } = this.messageQueue.shift();
            if (((_a = this.ws) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN) {
                this.ws.send(JSON.stringify(message));
            }
        }
    }
    setupWebSocketHandlers() {
        if (!this.ws)
            return;
        this.ws.onopen = () => {
            // AlphaLend WebSocket connected successfully
            // Clear connection timeout
            if (this.connectionTimeout) {
                clearTimeout(this.connectionTimeout);
                this.connectionTimeout = null;
            }
            this.setConnectionState('connected');
            this.reconnectAttempts = 0;
            this.reconnectDelay = 1000; // Reset backoff delay
            this.lastHeartbeat = Date.now();
            // Start heartbeat
            this.startHeartbeat();
            // Process queued subscriptions
            this.processSubscriptionQueue();
            // Process queued messages
            this.processMessageQueue();
        };
        this.ws.onmessage = (event) => {
            this.lastHeartbeat = Date.now(); // Update heartbeat timestamp
            try {
                // Validate message format
                if (!event.data || typeof event.data !== 'string') {
                    console.warn('Invalid AlphaLend WebSocket message format:', event.data);
                    return;
                }
                const message = JSON.parse(event.data);
                // Validate message structure
                if (!message || typeof message !== 'object') {
                    console.warn('Invalid AlphaLend WebSocket message structure:', message);
                    return;
                }
                // Message received
                // The server sends messages with "event" property, map to room subscriptions
                const eventMessage = message;
                // Check for error messages
                if (eventMessage.event === 'error') {
                    console.error('❌ AlphaLend WebSocket Error:', message.data);
                    this.handleServerError(message.data);
                    return;
                }
                // Handle subscription acknowledgments
                if (eventMessage.event === 'subscribed' || eventMessage.event === 'subscription_ack') {
                    this.handleSubscriptionAck(eventMessage);
                    return;
                }
                // Check for any market_details related messages
                if (eventMessage.event && message.data) {
                    const eventType = eventMessage.event;
                    let roomName = null;
                    // Map event types to room subscriptions based on actual server behavior
                    if (eventType === 'market_data_high_freq' || eventType === 'market_data_low_freq') {
                        roomName = 'market_data';
                    }
                    else if (eventType.startsWith('portfolio:') &&
                        (eventType.endsWith('_high_freq') || eventType.endsWith('_low_freq'))) {
                        // Extract position cap ID from event like "portfolio:0x123_high_freq" or "portfolio:0x123_low_freq"
                        const match = eventType.match(/^portfolio:(.+)_(?:high|low)_freq$/);
                        if (match) {
                            roomName = `portfolio:${match[1]}`;
                            // Portfolio event received
                        }
                    }
                    else if (eventType.startsWith('market_details:')) {
                        // Event like "market_details:1" maps to room "market_details:1"
                        roomName = eventType;
                        // Market details event received
                    }
                    if (roomName) {
                        // Check for duplicate messages
                        if (this.isDuplicateMessage(roomName, message)) {
                            // Skipping duplicate message
                            return;
                        }
                        // Add the room property to the message for consistency
                        eventMessage.room = roomName;
                        this.saveToCache(roomName, message);
                        // Use debounced callbacks for better performance
                        if (this.roomCallbacks.has(roomName)) {
                            const callbacks = this.roomCallbacks.get(roomName);
                            this.debounceCallback(roomName, message, callbacks, false);
                        }
                        if (this.roomMessageCallbacks.has(roomName)) {
                            const callbacks = this.roomMessageCallbacks.get(roomName);
                            this.debounceCallback(roomName, message, callbacks, true);
                        }
                    }
                    // Handle account_history events (no room mapping needed, just dispatch)
                    if (eventType.startsWith('account_history:')) {
                        this.dispatchToMessageListeners(message);
                    }
                    // Handle export events (export_chunk, export_complete)
                    if (eventType.startsWith('export_chunk:') || eventType.startsWith('export_complete:')) {
                        this.dispatchToMessageListeners(message);
                    }
                    // Dispatch to any other pattern-based listeners
                    this.dispatchToMessageListeners(message);
                }
            }
            catch (error) {
                console.error('Error parsing AlphaLend WebSocket message:', error);
                // Don't close connection for parsing errors, just skip the message
            }
        };
        this.ws.onclose = (event) => {
            // AlphaLend WebSocket closed
            this.ws = null;
            this.clearAllTimers();
            // Determine if this was an expected close or error
            const wasCleanClose = event.code === 1000 || event.code === 1001;
            if (wasCleanClose) {
                this.setConnectionState('disconnected');
            }
            else {
                this.setConnectionState('failed');
                // Reconnect if should be connected and haven't exceeded max attempts
                if (this.isTabVisible && this.isUserActive && !this.isDestroyed) {
                    this.scheduleReconnect();
                }
            }
        };
        this.ws.onerror = (error) => {
            var _a;
            console.warn('AlphaLend WebSocket error:', error);
            console.warn('AlphaLend WebSocket state:', (_a = this.ws) === null || _a === void 0 ? void 0 : _a.readyState);
            console.warn('AlphaLend WebSocket URL:', ALPHALEND_WS_URL);
            // Don't immediately fail - let onclose handle the reconnection
            // This prevents double reconnection attempts
        };
    }
    handleServerError(errorData) {
        console.error('Server error received:', errorData);
        // Could implement specific error handling based on error type
        // For now, just log it
    }
    handleSubscriptionAck(_message) {
        // Handle subscription acknowledgments from server
        // Subscription acknowledged
        // Could track which subscriptions are confirmed
    }
    disconnect() {
        this.clearAllTimers();
        this.setConnectionState('disconnected');
        if (this.ws) {
            this.ws.close(1000, 'Client disconnect'); // Clean close
            this.ws = null;
        }
    }
    subscribe(room, callback) {
        if (!this.roomCallbacks.has(room)) {
            this.roomCallbacks.set(room, new Set());
            // Send subscription to server if connected
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.sendSubscription(room);
            }
        }
        this.roomCallbacks.get(room).add(callback);
        // Ensure connection if user is active and tab is visible (with small delay for server startup)
        if (!this.ws && this.isTabVisible && this.isUserActive) {
            setTimeout(() => {
                if (!this.ws && this.isTabVisible && this.isUserActive) {
                    this.connect();
                }
            }, 100); // 100ms delay to allow server to fully start
        }
        // Return cached data immediately to avoid blank pages
        const cachedData = this.getCachedData(room);
        if (cachedData) {
            try {
                callback(cachedData.message.data);
            }
            catch (error) {
                console.error(`Error calling alphalend callback with cached data for room ${room}:`, error);
            }
        }
    }
    subscribeMessage(room, callback) {
        if (!this.roomCallbacks.has(room) && !this.roomMessageCallbacks.has(room)) {
            this.roomMessageCallbacks.set(room, new Set());
            // Send subscription to server if connected
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.sendSubscription(room);
            }
        }
        else if (!this.roomMessageCallbacks.has(room)) {
            this.roomMessageCallbacks.set(room, new Set());
        }
        this.roomMessageCallbacks.get(room).add(callback);
        // Ensure connection if user is active and tab is visible (with small delay for server startup)
        if (!this.ws && this.isTabVisible && this.isUserActive) {
            setTimeout(() => {
                if (!this.ws && this.isTabVisible && this.isUserActive) {
                    this.connect();
                }
            }, 100); // 100ms delay to allow server to fully start
        }
        // Return cached data immediately to avoid blank pages
        const cachedData = this.getCachedData(room);
        if (cachedData) {
            try {
                callback(cachedData.message);
            }
            catch (error) {
                console.error(`Error calling alphalend callback with cached data for room ${room}:`, error);
            }
        }
    }
    unsubscribe(room, callback) {
        if (this.roomCallbacks.has(room)) {
            this.roomCallbacks.get(room).delete(callback);
            // If no more callbacks, unsubscribe from server
            if (this.roomCallbacks.get(room).size === 0 && !this.roomMessageCallbacks.has(room)) {
                this.roomCallbacks.delete(room);
                if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                    this.sendUnsubscription(room);
                }
            }
        }
    }
    unsubscribeMessage(room, callback) {
        if (this.roomMessageCallbacks.has(room)) {
            this.roomMessageCallbacks.get(room).delete(callback);
            // If no more callbacks, unsubscribe from server
            if (this.roomMessageCallbacks.get(room).size === 0 &&
                (!this.roomCallbacks.has(room) || this.roomCallbacks.get(room).size === 0)) {
                this.roomMessageCallbacks.delete(room);
                if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                    this.sendUnsubscription(room);
                }
            }
        }
    }
    sendSubscription(room) {
        // Sending subscription
        let subscriptionMessage;
        // Handle special subscription formats
        if (room.startsWith('market_details:')) {
            const marketId = room.split(':')[1];
            subscriptionMessage = {
                action: 'subscribe',
                room: 'market_details',
                market_id: parseInt(marketId, 10),
            };
        }
        else if (room.startsWith('portfolio:')) {
            const positionCapId = room.split(':')[1];
            subscriptionMessage = {
                action: 'subscribe',
                room: 'portfolio',
                position_cap_id: positionCapId,
            };
        }
        else if (room.startsWith('account_history:')) {
            const positionCapId = room.split(':')[1];
            subscriptionMessage = {
                action: 'subscribe',
                room: 'account_history',
                position_cap_id: positionCapId,
            };
        }
        else {
            // Standard room subscription
            subscriptionMessage = { action: 'subscribe', room };
        }
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            // Sending subscription message
            this.ws.send(JSON.stringify(subscriptionMessage));
        }
        else if (!this.isOnline) {
            // Add to offline queue if network is offline
            this.addToOfflineQueue('subscribe', subscriptionMessage);
        }
        else {
            console.warn(`Cannot send subscription for ${room}: WebSocket not ready`);
        }
    }
    sendUnsubscription(room) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            // Handle special unsubscription formats
            if (room.startsWith('market_details:')) {
                const marketId = room.split(':')[1];
                this.ws.send(JSON.stringify({
                    action: 'unsubscribe',
                    room: 'market_details',
                    market_id: parseInt(marketId, 10),
                }));
            }
            else if (room.startsWith('portfolio:')) {
                const positionCapId = room.split(':')[1];
                this.ws.send(JSON.stringify({
                    action: 'unsubscribe',
                    room: 'portfolio',
                    position_cap_id: positionCapId,
                }));
            }
            else if (room.startsWith('account_history:')) {
                const positionCapId = room.split(':')[1];
                this.ws.send(JSON.stringify({
                    action: 'unsubscribe',
                    room: 'account_history',
                    position_cap_id: positionCapId,
                }));
            }
            else {
                // Standard room unsubscription
                this.ws.send(JSON.stringify({ action: 'unsubscribe', room }));
            }
        }
    }
    // Public method to get connection status (useful for UI)
    getConnectionStatus() {
        return this.connectionState;
    }
    // Public method to get activity status (useful for diagnostic UI)
    getActivityStatus() {
        return {
            isUserActive: this.isUserActive,
            isTabVisible: this.isTabVisible,
        };
    }
    onConnectionStatusChange(callback) {
        this.connectionStatusCallbacks.add(callback);
        // Return unsubscribe function
        return () => {
            this.connectionStatusCallbacks.delete(callback);
        };
    }
    // Send transaction notification to AlphaLend WebSocket server
    sendTransactionNotification(marketId, positionCapId, txType) {
        const notification = {
            action: 'transaction',
            market_id: marketId,
            position_cap_id: positionCapId,
        };
        // Include tx_type if provided
        if (txType) {
            notification.tx_type = txType;
        }
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            try {
                this.ws.send(JSON.stringify(notification));
                console.log('📡 Transaction notification sent:', notification);
            }
            catch (error) {
                console.error('Error sending AlphaLend transaction notification:', error);
            }
        }
        else if (!this.isOnline) {
            // Add to offline queue if network is offline
            this.addToOfflineQueue('transaction', notification);
        }
        else {
            console.warn('AlphaLend WebSocket not connected, cannot send transaction notification');
        }
    }
    // Send claim rewards notification to AlphaLend WebSocket server
    sendClaimRewardsNotification(positionCapId) {
        const notification = {
            action: 'transaction',
            tx_type: 'claim_reward', // Note: server expects 'claim_reward' not 'claim_rewards'
            position_cap_id: positionCapId,
        };
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            try {
                this.ws.send(JSON.stringify(notification));
                // Claim rewards notification sent
            }
            catch (error) {
                console.error('Error sending AlphaLend claim rewards notification:', error);
            }
        }
        else if (!this.isOnline) {
            // Add to offline queue if network is offline
            this.addToOfflineQueue('claim_rewards', notification);
        }
        else {
            console.warn('AlphaLend WebSocket not connected, cannot send claim rewards notification');
        }
    }
    /**
     * Send a raw message to the WebSocket server
     * Used for request/response patterns like account history
     */
    sendMessage(message) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            try {
                this.ws.send(JSON.stringify(message));
            }
            catch (error) {
                console.error('Error sending message:', error);
            }
        }
        else if (!this.isOnline) {
            this.addToOfflineQueue('message', message);
        }
        else {
            // Queue the message to be sent when connected
            this.messageQueue.push({ room: 'custom', message });
        }
    }
    /**
     * Add a listener for messages matching a specific event pattern
     * Returns an unsubscribe function
     */
    addMessageListener(eventPattern, callback) {
        if (!this.messageListeners.has(eventPattern)) {
            this.messageListeners.set(eventPattern, new Set());
        }
        this.messageListeners.get(eventPattern).add(callback);
        return () => {
            const listeners = this.messageListeners.get(eventPattern);
            if (listeners) {
                listeners.delete(callback);
                if (listeners.size === 0) {
                    this.messageListeners.delete(eventPattern);
                }
            }
        };
    }
    /**
     * Subscribe to account history room for a position
     */
    subscribeToAccountHistory(positionCapId) {
        const room = `account_history:${positionCapId}`;
        // Track subscription
        this.subscriptionQueue.add(room);
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({
                action: 'subscribe',
                room: 'account_history',
                position_cap_id: positionCapId,
            }));
        }
        else if (!this.isOnline) {
            this.addToOfflineQueue('subscribe', {
                action: 'subscribe',
                room: 'account_history',
                position_cap_id: positionCapId,
            });
        }
        // Ensure connection
        if (!this.ws && this.isTabVisible && this.isUserActive) {
            setTimeout(() => {
                if (!this.ws && this.isTabVisible && this.isUserActive) {
                    this.connect();
                }
            }, 100);
        }
    }
    /**
     * Unsubscribe from account history room
     */
    unsubscribeFromAccountHistory(positionCapId) {
        const room = `account_history:${positionCapId}`;
        this.subscriptionQueue.delete(room);
        this.activeSubscriptions.delete(room);
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({
                action: 'unsubscribe',
                room: 'account_history',
                position_cap_id: positionCapId,
            }));
        }
    }
    /**
     * Dispatch message to pattern-based listeners
     */
    dispatchToMessageListeners(message) {
        const eventMessage = message;
        if (!eventMessage.event)
            return;
        this.messageListeners.forEach((callbacks, pattern) => {
            var _a;
            // Check exact match or prefix match
            if (eventMessage.event === pattern || ((_a = eventMessage.event) === null || _a === void 0 ? void 0 : _a.startsWith(pattern + ':'))) {
                callbacks.forEach((callback) => {
                    try {
                        callback(message);
                    }
                    catch (error) {
                        console.error(`Error in message listener for ${pattern}:`, error);
                    }
                });
            }
        });
    }
}
// Simple export
export const alphalendWebSocketService = new AlphalendWebSocketService();
